import { useState } from "react";
import { Modal } from "../shared/Modal";
import { FormInput } from "../shared/FormInput";
import { Button } from "../shared/Button";
import { useApiCall } from "../../lib/useToastError";
import { AdminTiers } from "../../lib/apiTyped";

// Creates a brand new tier doc + Stripe Product/Price in one shot. Slug
// must be unique and URL-safe (lowercase / digits / dashes) so it works
// as a Firestore doc id and a stable identifier in API surfaces.
//
// When `restrictedToOrgId` is set the modal becomes "custom plan for org"
// mode: the slug field is hidden and a unique org-scoped slug is generated
// automatically, and the resulting tier is only visible/assignable to that
// one org.
export function CreateTierModal({
  onClose,
  onSaved,
  restrictedToOrgId,
  orgName,
}: {
  onClose: () => void;
  onSaved: () => void;
  restrictedToOrgId?: string;
  orgName?: string;
}) {
  const customMode = !!restrictedToOrgId;
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [maxSeats, setMaxSeats] = useState("3");
  const [creditsM, setCreditsM] = useState("1");
  const [storageGB, setStorageGB] = useState("1");
  const [rpm, setRpm] = useState("120");
  const [artifacts, setArtifacts] = useState(true);
  const [prioritySupport, setPrioritySupport] = useState(false);
  const [busy, setBusy] = useState(false);

  const { run } = useApiCall();

  const slugLooksValid = customMode || /^[a-z0-9-]{2,40}$/.test(slug);
  const seatsValid = Number.isFinite(Number(maxSeats)) && Number(maxSeats) >= 1;
  const ready =
    !busy &&
    slugLooksValid &&
    name.trim().length > 0 &&
    Number.isFinite(Number(price)) &&
    Number(price) >= 0 &&
    seatsValid;

  async function submit() {
    if (!ready) return;
    setBusy(true);
    const finalSlug = customMode
      ? `custom-${restrictedToOrgId!.slice(-6)}-${Date.now().toString(36)}`
      : slug;
    const r = await run(
      () =>
        AdminTiers.create({
          slug: finalSlug,
          name: name.trim(),
          priceEurPerMonth: Number(price),
          maxSeats: Math.round(Number(maxSeats)),
          monthlyCreditsLimit: Math.round(Number(creditsM) * 1_000_000),
          storageBytesLimit: Math.round(Number(storageGB) * 1_073_741_824),
          requestsPerMinuteLimit: Math.round(Number(rpm)),
          features: { artifacts, prioritySupport },
          ...(customMode ? { restrictedToOrgId } : {}),
        }),
      {
        successMessage: customMode
          ? `Plan personalizado "${name.trim()}" creado para ${orgName ?? "la org"}.`
          : `Plan ${name.trim()} created and published in Stripe.`,
      },
    );
    setBusy(false);
    if (r) onSaved();
  }

  return (
    <Modal onClose={onClose} maxWidth="32rem">
      <h2 className="text-xl font-black mb-1 pr-6 text-ink">
        {customMode
          ? `Plan personalizado${orgName ? ` para ${orgName}` : ""}`
          : "Create new plan"}
      </h2>
      <p className="text-sm text-ink-3 mb-5">
        {customMode
          ? "Este plan sólo será visible y asignable a esta organización. Stripe creará un Product + Price dedicado al instante."
          : (
            <>
              The Product + Price will be created in Stripe automatically. The slug is
              permanent — choose it carefully (e.g.: <code>tier-3</code>,{" "}
              <code>enterprise</code>). Price is a flat monthly fee for the
              whole org (Stripe quantity is always 1). Credits and storage
              are TOTALS shared across the team. Requests/minute is the
              per-member rate cap.
            </>
          )}
      </p>

      <div className="space-y-4">
        <div className={customMode ? "" : "grid grid-cols-2 gap-3"}>
          {!customMode && (
            <FormInput
              label="Slug (id)"
              value={slug}
              onChange={(v) => setSlug(v.toLowerCase())}
              placeholder="tier-3"
            />
          )}
          <FormInput
            label="Display name"
            value={name}
            onChange={setName}
            placeholder={customMode ? "Plan ACME 2026" : "Enterprise"}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Price (€ / month)"
            value={price}
            onChange={setPrice}
            type="number"
          />
          <FormInput
            label="Max users"
            value={maxSeats}
            onChange={setMaxSeats}
            type="number"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Credits / month TOTAL (millions)"
            value={creditsM}
            onChange={setCreditsM}
            type="number"
          />
          <FormInput
            label="Storage TOTAL (GB)"
            value={storageGB}
            onChange={setStorageGB}
            type="number"
          />
        </div>
        <FormInput
          label="Requests / minute (per member)"
          value={rpm}
          onChange={setRpm}
          type="number"
        />

        <div className="space-y-2 pt-2">
          <ToggleRow
            label="Artifacts"
            checked={artifacts}
            onChange={setArtifacts}
          />
          <ToggleRow
            label="Priority support"
            checked={prioritySupport}
            onChange={setPrioritySupport}
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <Button variant="primary" onClick={submit} disabled={!ready} fullWidth>
          {busy ? "Creating…" : "Create plan"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm text-ink-2">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-ink"
      />
    </label>
  );
}
